import { MagnifyingGlass, Minus, Plus, Spinner } from "@phosphor-icons/react";
import { Tooltip } from "antd";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Document, DocumentProps, Page, pdfjs } from "react-pdf";
import { useEventListener } from "usehooks-ts";
import { When } from "x-wings";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDF_PAGES_SCALE_OFFSET = 0.05;

const DocLoadingLoader = () => (
    <div className="absolute inset-0 grid h-screen max-h-full place-items-center ">
        <Spinner size={32} className="text-primary-600" />
    </div>
);

const PDFViewer: React.FC<{ className?: string; signedURL: string; isPDF: boolean }> = ({
    className,
    signedURL,
    isPDF
}) => {
    const [pdfPagesCount, setPdfPagesCount] = useState<number>();
    const [pdfPagesVisible, setPdfPagesVisible] = useState<number[]>([]);
    const [scale, setScale] = useState<number>(1);
    const containerRef = useRef<HTMLDivElement>(null);

    const pdfPageObserver = useMemo(() => {
        return new IntersectionObserver(
            (entries) => {
                entries.forEach((_) => {
                    const child = _.target.firstChild as HTMLDivElement;
                    if (!child) return;

                    const pageNumber = child.dataset.pageNumber ? parseInt(child.dataset.pageNumber) : 0;
                    setPdfPagesVisible((prev) => {
                        if (pageNumber === 0) return prev;
                        if (_.isIntersecting) {
                            if (!prev.includes(pageNumber)) {
                                return [...prev, pageNumber].sort((a, b) => a - b);
                            }
                            return prev;
                        } else {
                            return prev.filter((_) => _ !== pageNumber).sort((a, b) => a - b);
                        }
                    });
                });
            },
            { threshold: 0.1 }
        );
    }, []);

    const handlePdfLoadSuccess: DocumentProps["onLoadSuccess"] = (_) => {
        setPdfPagesCount(_.numPages);
    };

    const handleScaleIncrease = () => {
        setScale((prev) => prev + PDF_PAGES_SCALE_OFFSET);
    };

    const handleScaleDecrease = () => {
        setScale((prev) => (prev - PDF_PAGES_SCALE_OFFSET > 0 ? prev - PDF_PAGES_SCALE_OFFSET : prev));
    };

    let intervalId: NodeJS.Timer;
    const handleLongPress =
        (callback: () => void): React.MouseEventHandler<SVGSVGElement> =>
            (event) => {
                const handleMouseDown = () => {
                    intervalId = setInterval(callback, 100); // set the interval duration to 100ms
                };

                const handleMouseUp = () => {
                    clearInterval(intervalId as NodeJS.Timeout);
                };

                event.target.addEventListener("mousedown", handleMouseDown);
                event.target.addEventListener("mouseup", handleMouseUp);
                event.target.addEventListener("mouseleave", handleMouseUp);

                return () => {
                    event.target.removeEventListener("mousedown", handleMouseDown);
                    event.target.removeEventListener("mouseup", handleMouseUp);
                    event.target.removeEventListener("mouseleave", handleMouseUp);
                };
            };

    useEffect(() => {
        setScale(1);
    }, [signedURL]);

    useEventListener("keydown", (event) => {
        if (event.ctrlKey === true && window.document.activeElement === window.document.body) {
            switch (event.key) {
                case "=":
                    handleScaleIncrease();
                    event.preventDefault();
                    break;
                case "-":
                    handleScaleDecrease();
                    event.preventDefault();
                    break;
                case "0":
                    setScale(1);
                    event.preventDefault();
                    break;

                default:
                    break;
            }
        }
    });

    return (
        <div className={clsx("scrollbar-hidden relative grid h-full w-full overflow-scroll", className)}>
            {signedURL && (
                <div ref={containerRef} className="h-inherit scrollbar-hidden relative overflow-auto px-3 py-8">
                    <When isTrue={isPDF}>
                        <Document
                            className="document flex h-full w-full flex-col items-center gap-8"
                            file={signedURL}
                            loading={<DocLoadingLoader />}
                            onLoadSuccess={handlePdfLoadSuccess}>
                            {pdfPagesCount &&
                                Array.from({ length: pdfPagesCount }, (_, key) => (
                                    <div
                                        ref={(ele) => {
                                            if (ele) {
                                                pdfPageObserver.observe(ele);
                                            }
                                        }}
                                        style={{ transform: `scale(${scale})` }}
                                        className="mx-auto max-w-full transition-[transform]">
                                        <Page
                                            key={key}
                                            pageIndex={key}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                            loading={
                                                <div className="h-screen max-h-full">
                                                    <DocLoadingLoader />
                                                </div>
                                            }
                                            _className="document-preview"
                                            scale={1}
                                        />
                                    </div>
                                ))}
                        </Document>
                    </When>
                    <When isTrue={!isPDF}>
                        <picture>
                            <img
                                src={signedURL}
                                alt="Document"
                                loading="lazy"
                                style={{ transform: `scale(${scale})` }}
                                className="mx-auto max-w-full transition-[transform]"
                            />
                        </picture>
                    </When>
                </div>
            )}

            <div className="border-y-solid absolute col-start-1 col-end-[-1] row-start-1 row-end-[-1] mb-6 select-none self-end justify-self-center rounded-xl border-y border-white/[15%] bg-gray-900/80 py-1.5 pl-3.5 pr-2 shadow-lg">
                <div className="flex items-center gap-3">
                    {!!pdfPagesCount && (
                        <>
                            <span className="text-sm text-gray-200 drop-shadow-[0px_1px_1px_rgba(0_0_0/0.30)]">
                                Page {pdfPagesVisible.length === 0 ? "0" : pdfPagesVisible.join(" - ")} of{" "}
                                {pdfPagesCount}
                            </span>
                            <div className="border-l-solid border-r-solid h-4 border-l border-r border-l-gray-600 border-r-white/[15%]" />
                        </>
                    )}
                    <Tooltip title="Zoom out (Ctrl + -)">
                        <Minus
                            role="button"
                            size={28}
                            className="rounded-lg p-1 text-gray-200 drop-shadow-[0px_1px_1px_rgba(0_0_0/0.30)] transition-all hover:bg-gray-600 hover:text-white"
                            onClick={handleScaleDecrease}
                            onMouseDown={handleLongPress(handleScaleDecrease)}
                        />
                    </Tooltip>
                    <Tooltip title="Zoom reset (Ctrl + 0)">
                        <MagnifyingGlass
                            size={28}
                            onClick={() => setScale(1)}
                            className="cursor-pointer p-1 text-gray-200 drop-shadow-[0px_1px_1px_rgba(0_0_0/0.30)]"
                        />
                    </Tooltip>
                    <Tooltip title="Zoom in (Ctrl + +)">
                        <Plus
                            role="button"
                            size={28}
                            className="rounded-lg p-1 text-gray-200 drop-shadow-[0px_1px_1px_rgba(0_0_0/0.30)] transition-all hover:bg-gray-600 hover:text-white"
                            onClick={handleScaleIncrease}
                            onMouseDown={handleLongPress(handleScaleIncrease)}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

export default PDFViewer;

